import { AlertType } from '../lib';
import { SRP, onColor, cssVar } from './tokens';

/**
 * @typedef {{main: string, dark: string, text: string, light: string}} Color
 */

/*
 * Status colors.
 *
 * `main` is the Standard Response Protocol color for the status — fixed by the
 * protocol, not a design choice. `text` is derived from it rather than hand-set
 * so it stays legible: the set spans #FFDC00 to #B400FF, where neither white
 * nor near-black works throughout.
 *
 * These are display values only. Changing them does not affect the
 * `dashboardStatus` string sent to the server or the IoT value published to a
 * badge or hub.
 */

/** @type {Color} */
export const RED = {
  main: SRP.RED,
  dark: '#8f0000',
  text: onColor(SRP.RED),
  light: '#FF9999'
};

/** @type {Color} */
export const YELLOW = {
  main: SRP.YELLOW,
  dark: '#8a7600',
  text: onColor(SRP.YELLOW),
  light: '#FFF199'
};

/** @type {Color} */
export const GREEN = {
  main: SRP.GREEN,
  dark: '#00802a',
  text: onColor(SRP.GREEN),
  light: '#B6FFCC'
};

/** @type {Color} */
export const BLUE = {
  main: SRP.BLUE,
  dark: '#003a94',
  text: onColor(SRP.BLUE),
  light: '#87D3FD'
};

/** @type {Color} */
export const PURPLE = {
  main: SRP.PURPLE,
  dark: '#660091',
  text: onColor(SRP.PURPLE),
  light: '#D8BFD8'
};

/**
 * Shelter's color. Unused until the Shelter alert type exists on the server —
 * defined here so the palette is complete and the eventual addition is a
 * one-line change to AlertTypeObjs.
 * @type {Color}
 */
export const ORANGE = {
  main: SRP.ORANGE,
  dark: '#9c4700',
  text: onColor(SRP.ORANGE),
  light: '#FFCFA3'
};

/**
 * Normal. Not an SRP color — nothing is happening, so this reads as a neutral
 * surface rather than a signal.
 * @type {Color}
 */
export const DEFAULT = {
  main: '#bfc1b7',
  dark: '#4d4f46',
  text: '#23251d',
  light: '#dcdfd2'
};

/** @type {Color} */
export const GREY = {
  main: '#9b9c92',
  dark: '#23251d',
  light: '#dcdfd2',
  text: '#ffffff'
};

/**
 * Legacy grey ramp, kept so the ~40 existing inline-style call sites keep
 * working. Each step maps onto a v7 surface token, so these follow the night
 * theme. New code should use `T` from constants/tokens.js instead.
 */
export const GREY_SCALE = [
  cssVar('canvas'),
  cssVar('surface-soft'),
  cssVar('hairline-soft'),
  cssVar('surface-soft'),
  cssVar('muted'),
  cssVar('body'),
  cssVar('ink')
];

// Alert Types
//
// `shouldAlertSchool` mirrors the server's AlertType rows: these put the whole
// school into the status, and only these can be run as a drill.
export const AlertTypeObjs = {
  EMERGENCY: {
    dashboardStatus: 'Emergency',
    color: RED,
    id: 10,
    shouldAlertSchool: true
  },
  ADMINISTRATOR: {
    dashboardStatus: 'Admin',
    color: YELLOW,
    id: 8
  },
  MEDICAL: {
    dashboardStatus: 'Medical',
    color: BLUE,
    id: 6
  },
  HOLD: {
    dashboardStatus: 'Hold',
    color: PURPLE,
    id: 11,
    shouldAlertSchool: true
  },
  ALL_CLEAR: {
    dashboardStatus: 'All Clear',
    color: GREEN,
    id: 4
  },
  NORMAL: {
    dashboardStatus: 'Normal',
    color: DEFAULT,
    id: 2
  },
  UNKNOWN: {
    dashboardStatus: 'Unknown',
    color: GREY,
    id: 0
  }
};

const alertTypes = {};
for (const type in AlertTypeObjs) {
  alertTypes[type] = AlertType.thaw(AlertTypeObjs[type]);
}
export const AlertTypes = alertTypes;

// List Views
export const DeviceTypes = {
  STAFF: 'Staff',
  ROOMS: 'Rooms',
  HUBS: 'Hubs'
};
export const DEVICE_TYPES = [
  DeviceTypes.STAFF,
  DeviceTypes.ROOMS,
  DeviceTypes.HUBS
];

export const HubTypes = {
  ROOM: 'Room',
  DOOR: 'Door',
  HALLWAY: 'Hallway'
};

export const UserTypes = {
  SUPER: {
    name: 'Super',
    index: 4
  },
  IT: {
    name: 'IT',
    index: 3
  },
  ADMIN: {
    name: 'Admin',
    index: 2
  },
  STAFF: {
    name: 'Staff',
    index: 1
  }
};

export const ValidCreateUserTypes = {
  ADMIN: 'Admin'
};

/**
 * @param {any} obj
 * @return {obj is Exclude<any, undefined | null>}
 */
export const isNotNull = (obj) => {
  return obj !== null && obj !== undefined;
};


/**
 * @param {any} variable
 * @return {variable is (null | undefined)}
 */
export function isNull(variable) {
  return variable === null || variable === undefined;
}

export function isValidPassword(password) {
  if (password.length < 8) {
    return false;
  }

  var hasUpperCase = /[A-Z]/.test(password);
  var hasLowerCase = /[a-z]/.test(password);
  var hasNumbers = /\d/.test(password);
  var hasNonalphas = /\W/.test(password);

  if (!(hasUpperCase && hasLowerCase && hasNumbers && hasNonalphas)) {
    return false;
  }

  return true;
}

export function isEmail(email) {
  const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(email);
}

export function isValidPhoneNumber(phoneNumber) {
  var re = /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/im;
  return re.test(phoneNumber);
}

export const HelpDescriptions = {
  TEMP: {
    name: 'Temporary',
    body: 'This is a temporary holder for the actual description.'
  }
};

/**
 * @type {Record<string, {name: string, value: function, values?: {name: string, value: any, active: boolean, nullMatch?: boolean}[], autoPopulate?: { name: string, value: any }}[]>}
 */
export const FilterOptions = {
  USER_LIST: [
    {
      'name': 'User Type',
      /** @param {import('../lib').User} user */
      value: (user) => user.getUserType(),
      'values': [
        {
          'name': 'Staff',
          'value': 'Staff',
          'active': false
        },
        {
          'name': 'Admin',
          'value': 'Admin',
          'active': false
        },
        {
          'name': 'IT',
          'value': 'IT',
          'active': false
        },
        {
          'name': 'Super',
          'value': 'Super',
          'active': false
        },
      ]
    },
    {
      'name': 'Activity',
      /** @param {import('../lib').User} user */
      value: (user) => user.getIsActive(),
      'values': [
        {
          'name': 'Active',
          'value': true,
          'active': false
        },
        {
          'name': 'Inactive',
          'value': false,
          'active': false
        }
      ]
    },
    {
      'name': '2FA Setup',
      /** @param {import('../lib').User} user */
      value: (user) => user.getMultiFactorSetupRequired(),
      'values': [
        {
          'name': 'Yes',
          'value': 0,
          'active': false
        },
        {
          'name': 'No',
          'value': 1,
          'active': false
        }
      ]
    },
    {
      'name': 'Password Changed',
      /** @param {import('../lib').User} user */
      value: (user) => user.getPasswordResetRequired(),
      'values': [
        {
          'name': 'Yes',
          'value': 0,
          'active': false
        },
        {
          'name': 'No',
          'value': 1,
          'active': false
        }
      ]
    }
  ],
  ROOM_LIST: [
    {
      'name': 'Status',
      /** @param {import('../lib').Room} room */
      value: (room) => room.getAlertStatusID(),
      'values': [
        {
          'name': 'Emergency',
          'value': 9,
          'active': false
        },
        {
          'name': 'Admin',
          'value': 7,
          'active': false
        },
        {
          'name': 'Medical',
          'value': 5,
          'active': false
        },
        {
          'name': 'All Clear',
          'value': 3,
          'active': false
        },
        {
          'name': 'Normal',
          'value': 2,
          'active': false
        },
        {
          'name': 'Unknown',
          'value': 0,
          'active': false,
          'nullMatch': true
        }
      ]
    }
  ],
  STAFF_LIST: [
    // ----- THE BELOW SECTION ISN'T CURRENTLY NEEDED BUT MAY BE LATER ---
    // {
    //   'name': 'Status',
    //   /** @param {import('../lib').StaffMember} staff */
    //   value: (staff) => {
    //     /** @type {import('../lib').AlertUpdate[] | null} */
    //     const alertUpdates = staff.getAlertUpdates();
    //     if (alertUpdates != null && alertUpdates.length > 0) {
    //       alertUpdates?.reduce((mostRecent, curr) => {
    //         if (curr.getTimestamp().toMillis() > mostRecent.getTimestamp().toMillis()) {
    //           return curr;
    //         }
    //         else {
    //           return mostRecent;
    //         }
    //       }, alertUpdates[0]);
    //     }
    //     else {
    //       return null;
    //     }
    //   },
    //   'values': [
    //     {
    //       'name': 'Emergency',
    //       'value': 9,
    //       'active': false
    //     },
    //     {
    //       'name': 'Admin',
    //       'value': 7,
    //       'active': false
    //     },
    //     {
    //       'name': 'Medical',
    //       'value': 5,
    //       'active': false
    //     },
    //     {
    //       'name': 'All Clear',
    //       'value': 3,
    //       'active': false
    //     },
    //     {
    //       'name': 'Normal',
    //       'value': 2,
    //       'active': false
    //     },
    //     {
    //       'name': 'Unknown',
    //       'value': 0,
    //       'active': false,
    //       'nullMatch': true
    //     }
    //   ]
    // },
    {
      'name': 'Badge Status',
      /** @param {import('../lib').StaffMember} staff */
      value: (staff) => {
        /** @type {import('../lib').Badge}*/
        const badge = staff.getBadge();
        if (badge != null) {
          return badge.getIsConnected();
        }
        else {
          return null;
        }
      },
      'values': [
        {
          'name': 'Connected',
          'value': true,
          'active': false
        },
        {
          'name': 'Not Connected',
          'value': false,
          'active': false
        },
        {
          'name': 'Not Assigned',
          'value': null,
          'active': false
        }
      ]
    },
    {
      name: 'Badge Power',
      /** @param {import('../lib').StaffMember} staff */
      value: (staff) => {
        /** @type {import('../lib').Badge}*/
        const badge = staff.getBadge();
        if (badge?.getBatteryLevel() != null) {
          return badge.getBatteryLevel() < lowBatteryPercent();
        }
        else {
          return null;
        }
      },
      'values': [
        {
          'name': 'Low',
          'value': true,
          'active': false
        },
        {
          'name': 'OK',
          'value': false,
          'active': false
        },
        {
          'name': 'Unknown',
          'value': null,
          'active': false
        }
      ]
    }
  ],
  HUB_LIST: [
    {
      'name': 'Hub Type',
      /** @param {import('../lib').Hub} hub */
      value: (hub) => hub.getHubType(),
      'values': [
        {
          'name': 'Room',
          'value': 'Room',
          'active': false
        },
        {
          'name': 'Door',
          'value': 'Door',
          'active': false
        },
        {
          'name': 'Hallway',
          'value': 'Hallway',
          'active': false
        }
      ]
    },
    {
      'name': 'Status',
      /** @param {import('../lib').Hub} hub */
      value: (hub) => hub.getRoom()?.getAlertStatusID() ?? null,
      'values': [
        {
          'name': 'Emergency',
          'value': 9,
          'active': false
        },
        {
          'name': 'Admin',
          'value': 7,
          'active': false
        },
        {
          'name': 'Medical',
          'value': 5,
          'active': false
        },
        {
          'name': 'All Clear',
          'value': 3,
          'active': false
        },
        {
          'name': 'Normal',
          'value': 2,
          'active': false
        },
        {
          'name': 'Unknown',
          'value': 0,
          'active': false,
          'nullMatch': true
        }
      ]
    },
    {
      'name': 'Connected',
      /** @param {import('../lib').Hub} hub */
      value: (hub) => hub.getIsConnected(),
      'values': [
        {
          'name': 'Connected',
          'value': true,
          'active': false
        },
        {
          'name': 'Not Connected',
          'value': false,
          'active': false,
          'nullMatch': true
        }
      ]
    },
    {
      'name': 'Firmware Version',
      /** @param {import('../lib').Hub} hub */
      value: (hub) => hub.getFirmwareVersion(),
      'autoPopulate': {
        name: 'v#{value}',
        value: '#{value}'
      }
    }
  ]
  ,
  /**
   * The design gives every device tab its own filters. Badges were the one
   * tab reachable in the UI without a set, so the Filter control simply did
   * not appear there.
   */
  BADGE_LIST: [
    {
      'name': 'Badge Status',
      /** @param {import('../lib').Badge} badge */
      value: (badge) => badge.getIsConnected(),
      'values': [
        {
          'name': 'Connected',
          'value': true,
          'active': false
        },
        {
          'name': 'Not Connected',
          'value': false,
          'active': false,
          'nullMatch': true
        }
      ]
    },
    {
      name: 'Badge Power',
      /** @param {import('../lib').Badge} badge */
      value: (badge) => {
        const level = badge.getBatteryLevel();
        return level == null ? null : level < lowBatteryPercent();
      },
      'values': [
        {
          'name': 'Low',
          'value': true,
          'active': false
        },
        {
          'name': 'OK',
          'value': false,
          'active': false
        },
        {
          'name': 'Unknown',
          'value': null,
          'active': false
        }
      ]
    },
    {
      'name': 'Firmware Version',
      /** @param {import('../lib').Badge} badge */
      value: (badge) => badge.getFirmwareVersion(),
      'autoPopulate': {
        name: 'v#{value}',
        value: '#{value}'
      }
    },
    {
      'name': 'Assignment',
      /** @param {import('../lib').Badge} badge */
      value: (badge) => badge.getBelongsTo() != null,
      'values': [
        {
          'name': 'Assigned',
          'value': true,
          'active': false
        },
        {
          'name': 'Unassigned',
          'value': false,
          'active': false,
          'nullMatch': true
        }
      ]
    }
  ],
  GATEWAY_LIST: [
    {
      name: 'Gateway Status',
      /** @param {import('../lib').NetworkNode} node */
      value: (node) => node.getIsConnected(),
      values: [
        { name: 'Online', value: true, active: false },
        { name: 'Unreachable', value: false, active: false },
        { name: 'Not reported', value: null, active: false }
      ]
    }
  ],
  ANCHOR_LIST: [
    {
      name: 'Anchor Role',
      /** @param {import('../lib').NetworkNode} node */
      value: (node) => node.getModel(),
      autoPopulate: {
        name: '#{value}',
        value: '#{value}'
      }
    },
    {
      name: 'Mesh Status',
      /** @param {import('../lib').NetworkNode} node */
      value: (node) => node.getIsConnected(),
      values: [
        { name: 'On mesh', value: true, active: false },
        { name: 'Off mesh', value: false, active: false },
        { name: 'Not reported', value: null, active: false }
      ]
    },
    {
      name: 'Link Quality',
      /** @param {import('../lib').NetworkNode} node */
      value: (node) => node.getQualityBand(),
      values: [
        { name: 'Strong', value: 'strong', active: false },
        { name: 'Good', value: 'good', active: false },
        { name: 'Marginal', value: 'marginal', active: false },
        { name: 'Weak', value: 'weak', active: false }
      ]
    }
  ]
};

let lowBattery = 30;

/**
 * Battery below this reads as low. The school's LOW_BATTERY_PERCENT setting,
 * which also decides when low power notices go out; 30, the server's default,
 * until the first status poll brings it.
 */
export const lowBatteryPercent = () => lowBattery;

/** Called from the status poll with the server's threshold. */
export function setLowBatteryPercent(percent) {
  if (Number.isFinite(percent) && percent > 0) lowBattery = percent;
}

export const MOBILE_WIDTH = 970;
export const FULL_WIDTH = 1280;

/**
 * @param {any} variable
 * @return {variable is string}
 */
export const isString = (variable) => {
  return typeof variable === 'string' || variable instanceof String;
};
